import {
  capAscent,
  drawPicture,
  edgeVignette,
  fitSize,
  rgba,
  tracked,
  trackedWidth,
  verticalScrim,
  type Ctx,
  type FontAt,
} from '../canvas';
import { num, type Template, type Values } from '../types';

/* ══ SVART — full-bleed art over a heavy headline ═══════════════════
   Square, for Facebook. A picture across the top two thirds, dropped
   into black, with the event underneath: kicker, headline, set, date,
   venue, and a strip of four figures. Everything is measured off the
   headline's real cap height rather than fixed positions, so a long
   name pushes the rest around instead of colliding with it. */

const W = 1080;
const H = 1080;
const PAD = 56;
const BAND = 790; // how far down the picture reaches before it is all black
const AVAIL = W - PAD * 2;

const VOID: [number, number, number] = [8, 8, 10];
const GOLD: [number, number, number] = [232, 184, 75];
const INK: [number, number, number] = [242, 239, 233];
const INK2: [number, number, number] = [168, 164, 156];
const INK3: [number, number, number] = [110, 106, 99];

const anton: FontAt = (px) => `400 ${px}px Anton, Impact, sans-serif`;
const mono: FontAt = (px) => `500 ${px}px "IBM Plex Mono", ui-monospace, monospace`;
const arch: FontAt = (px) => `600 ${px}px Archivo, Helvetica, sans-serif`;

/** The vertical wash that carries the picture down into the black. */
const SCRIM: [number, number][] = [
  [0, 0.62],
  [0.22, 0.05],
  [0.44, 0.18],
  [0.76, 0.93],
  [0.96, 1],
  [1, 1],
];

/** Bottom-anchored block of small print. */
function footnote(ctx: Ctx, text: string, x: number, bottom: number, align: CanvasTextAlign): void {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 3);
  ctx.font = mono(10.5);
  ctx.fillStyle = rgba(INK2, 0.62);
  ctx.textAlign = align;
  lines.forEach((line, i) => {
    ctx.fillText(line, x, bottom - (lines.length - 1 - i) * 15);
  });
  ctx.textAlign = 'left';
}

/** Gold pill behind a value, the way a highlighted figure is marked. */
function pill(ctx: Ctx, x: number, y: number, w: number, h: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 3);
  ctx.fillStyle = rgba(GOLD, 0.22);
  ctx.fill();
  ctx.strokeStyle = rgba(GOLD, 0.47);
  ctx.lineWidth = 1;
  ctx.stroke();
}

type Cell = { k: string; v: string; hi: boolean };

/** The four figures, shrunk together until they fit the width. */
function drawStrip(ctx: Ctx, cells: Cell[]): void {
  if (!cells.length) return;

  const widthAt = (vp: number, kp: number, gap: number): number => {
    let total = gap * (cells.length - 1);
    for (const c of cells) {
      ctx.font = anton(vp);
      const vw = ctx.measureText(c.v).width;
      ctx.font = mono(kp);
      total += Math.max(vw, trackedWidth(ctx, c.k, 2.2));
    }
    return total;
  };

  let vp = 29;
  let kp = 11;
  let gap = 38;
  for (; vp > 16; vp -= 1) {
    kp = Math.max(9, Math.round((11 * vp) / 29));
    gap = Math.max(18, Math.round((38 * vp) / 29));
    if (widthAt(vp, kp, gap) <= AVAIL) break;
  }

  let x = PAD;
  for (const c of cells) {
    ctx.font = mono(kp);
    ctx.fillStyle = rgba(INK3, 1);
    const kw = trackedWidth(ctx, c.k, 2.2);
    tracked(ctx, x, 979, c.k, 2.2);

    ctx.font = anton(vp);
    const vw = ctx.measureText(c.v).width;
    if (c.hi) pill(ctx, x - 7, 988, vw + 14, 34);
    ctx.fillStyle = rgba(c.hi ? GOLD : INK, 1);
    ctx.fillText(c.v, x, 1016);

    x += Math.max(vw, kw) + gap;
  }
}

function draw(ctx: Ctx, v: Values, picture: HTMLImageElement | null): void {
  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = rgba(VOID, 1);
  ctx.fillRect(0, 0, W, H);

  /* ── picture ── */
  if (picture) {
    drawPicture(ctx, picture, W, BAND, {
      zoom: Math.max(0.5, num(v.zoom, 100) / 100),
      focus: Math.min(1, Math.max(0, num(v.focus, 18) / 100)),
    });
  }
  verticalScrim(ctx, 0, 0, W, BAND, VOID, SCRIM);
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, W, BAND);
  ctx.clip();
  edgeVignette(ctx, W, BAND, VOID);
  ctx.restore();

  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';

  /* ── top bar ── */
  ctx.font = mono(13);
  ctx.fillStyle = rgba(INK, 0.84);
  tracked(ctx, PAD, 44, (v.org ?? '').toUpperCase(), 3.9);

  const place = v.place ?? '';
  const placeW = trackedWidth(ctx, place, 3.9);
  tracked(ctx, W - PAD - placeW, 44, place, 3.9);
  if (place) {
    ctx.beginPath();
    ctx.arc(W - PAD - placeW - 17.5, 40, 4.5, 0, Math.PI * 2);
    ctx.fillStyle = rgba(GOLD, 1);
    ctx.fill();
  }

  /* ── headline block ──
     Drawn bottom-up off fixed baselines, then the kicker is hung off
     the headline's measured cap top so the gap is real at any size. */
  const headline = (v.headline ?? '').toUpperCase();
  const hp = fitSize(ctx, headline, AVAIL, anton, 196, 54);
  ctx.font = anton(hp);
  ctx.fillStyle = rgba(INK, 1);
  ctx.fillText(headline, PAD, 778);
  const capTop = 778 - capAscent(ctx, headline || 'H');

  const subhead = (v.subhead ?? '').toUpperCase();
  ctx.font = anton(fitSize(ctx, subhead, AVAIL, anton, 56, 22));
  ctx.fillStyle = rgba(GOLD, 1);
  ctx.fillText(subhead, PAD, 830);

  const kicker = (v.kicker ?? '').toUpperCase();
  const kp = fitSize(ctx, kicker, AVAIL, mono, 15, 9, 5.1);
  ctx.font = mono(kp);
  ctx.fillStyle = rgba(GOLD, 1);
  tracked(ctx, PAD, capTop - 30, kicker, 5.1);

  /* ── rule, fading out to the right ── */
  const rule = ctx.createLinearGradient(PAD, 0, PAD + AVAIL * 0.62, 0);
  rule.addColorStop(0, rgba(GOLD, 1));
  rule.addColorStop(1, rgba(GOLD, 0));
  ctx.fillStyle = rule;
  ctx.fillRect(PAD, 856, AVAIL * 0.62, 1);

  /* ── date ── */
  const date = (v.date ?? '').toUpperCase();
  ctx.font = anton(fitSize(ctx, date, AVAIL, anton, 42, 18));
  ctx.fillStyle = rgba(INK, 1);
  ctx.fillText(date, PAD, 916);

  /* ── venue · time ── */
  ctx.font = arch(19);
  let x = PAD;
  for (const [part, boxed] of [
    [v.venue ?? '', true],
    [v.venue && v.time ? '  ·  ' : '', false],
    [v.time ?? '', true],
  ] as [string, boolean][]) {
    if (!part) continue;
    const pw = ctx.measureText(part).width;
    if (boxed) pill(ctx, x - 5, 922, pw + 10, 27);
    ctx.fillStyle = rgba(INK2, 1);
    ctx.fillText(part, x, 940);
    x += pw;
  }

  /* ── figures ── */
  drawStrip(
    ctx,
    ([1, 2, 3, 4] as const)
      .map((i) => ({
        k: (v[`s${i}k`] ?? '').toUpperCase(),
        v: (v[`s${i}v`] ?? '').toUpperCase(),
        hi: v[`s${i}hi`] === '1',
      }))
      .filter((c) => c.k || c.v),
  );

  /* ── small print ── */
  footnote(ctx, v.credit ?? '', PAD, 1055, 'left');
  footnote(ctx, v.legal ?? '', W - PAD, 1055, 'right');
}

export const sealedDark: Template = {
  id: 'sealed-dark',
  name: 'Svart — heilmynd',
  blurb: 'Ferningur fyrir Facebook. Heilmynd efst, þung fyrirsögn og fjórar tölur neðst.',
  width: W,
  height: H,
  usesPicture: true,
  fonts: ['400 196px Anton', '600 19px Archivo', '500 13px "IBM Plex Mono"'],
  groups: [
    {
      title: 'Efst',
      fields: [
        { key: 'org', label: 'Félagið', kind: 'text', width: 'half' },
        { key: 'place', label: 'Staður', kind: 'text', width: 'half' },
      ],
    },
    {
      title: 'Fyrirsögn',
      fields: [
        { key: 'kicker', label: 'Yfirlína', kind: 'text', hint: 'Spilið — t.d. Magic: The Gathering' },
        { key: 'headline', label: 'Fyrirsögn', kind: 'text', hint: 'Formatið. Minnkar sjálfkrafa ef það er langt.' },
        { key: 'subhead', label: 'Undirlína', kind: 'text', hint: 'Settið eða tilefnið' },
      ],
    },
    {
      title: 'Hvar og hvenær',
      fields: [
        { key: 'date', label: 'Dagsetning', kind: 'text' },
        { key: 'venue', label: 'Staðsetning', kind: 'text', width: 'half' },
        { key: 'time', label: 'Tími', kind: 'text', width: 'half' },
      ],
    },
    {
      // Label and value pair up on a row; the highlight switches follow
      // together so the strip reads as a strip in the form too.
      title: 'Tölur',
      fields: [
        ...([1, 2, 3, 4] as const).flatMap((i) => [
          { key: `s${i}k`, label: `Heiti ${i}`, kind: 'text' as const, width: 'half' as const },
          { key: `s${i}v`, label: `Gildi ${i}`, kind: 'text' as const, width: 'half' as const },
        ]),
        ...([1, 2, 3, 4] as const).map((i) => ({
          key: `s${i}hi`,
          label: `Áhersla á ${i}`,
          kind: 'toggle' as const,
          width: 'half' as const,
        })),
      ],
    },
    {
      title: 'Mynd',
      fields: [
        { key: 'zoom', label: 'Stækkun', kind: 'range', min: 100, max: 220, step: 1, suffix: '%', width: 'half' },
        { key: 'focus', label: 'Hæð myndar', kind: 'range', min: 0, max: 100, step: 1, suffix: '%', width: 'half' },
      ],
    },
    {
      title: 'Smáaletur',
      fields: [
        { key: 'credit', label: 'Myndaskil', kind: 'lines', rows: 2, hint: 'Spilið og listamaðurinn. Ein lína hvor.' },
        { key: 'legal', label: 'Fyrirvari', kind: 'lines', rows: 3, hint: 'Hægri-jafnað. Wizards krefst þessa fyrir MTG-efni.' },
      ],
    },
  ],
  defaults: {
    org: 'Spilafélag Vestfjarða',
    place: 'Ísafjörður',
    kicker: 'Magic: The Gathering',
    headline: 'Sealed',
    subhead: '',
    date: '',
    venue: '',
    time: '',
    s1k: 'Verð',
    s1v: '',
    s1hi: '1',
    s2k: 'Innifalið',
    s2v: '6 pakkar',
    s2hi: '',
    s3k: 'Umferðir',
    s3v: '3 × best af 3',
    s3hi: '',
    s4k: 'Pláss',
    s4v: '',
    s4hi: '1',
    zoom: '100',
    focus: '18',
    credit: '',
    legal:
      'Magic: The Gathering er vörumerki Wizards of the Coast LLC.\nViðburðurinn er á vegum Spilafélags Vestfjarða og ekki á vegum Wizards of the Coast.',
  },
  draw,
};
